import { User } from "@/pages/Public/pages/Login/schema/login";
import { SenderCandidate } from "@/pages/Private/pages/Users/schema/get-users";
import { isMailboxHealthy } from "@/pages/Private/pages/List/components/CampaignOverview/utils/resolveMyDefaultSender.util";

/**
 * What the sender picker says about a sender: why a disabled one cannot be chosen, or, for
 * `linkedinDisconnected`, a warning on one who can still send by email.
 */
export type SenderNote =
	| "linkedinDisconnected"
	| "nothingConnected"
	| "noEmailForLead"
	| "noLinkedinForLead"
	| "noChannelForLead";

export interface SenderOption {
	id: number;
	firstName: string;
	lastName: string;
	/** The sending user, when the user can send at all (a mailbox or a connected LinkedIn). */
	user: User | null;
	/** Whether the sender can reach this lead on at least one channel. */
	enabled: boolean;
	note: SenderNote | null;
}

export interface LeadChannels {
	prospectHasEmail?: boolean;
	prospectHasLinkedinUrl?: boolean;
}

/** A mailbox that can send: one that needs a reconnect does not reach anybody (PREC-4312). */
export const hasMailbox = (user: User): boolean => (user.emails ?? []).some(isMailboxHealthy);

const hasLinkedin = (user: User): boolean => !!user.unipile?.isLoggedIn;

const namesOf = (person: {
	firstName?: string | null;
	lastName?: string | null;
}): Pick<SenderOption, "firstName" | "lastName"> => ({
	firstName: person.firstName ?? "",
	lastName: person.lastName ?? "",
});

/** Whether a sender can reach the lead, and what the picker says about them. */
const judgeSendingUser = (
	user: User,
	linkedinDisconnected: boolean,
	lead: LeadChannels
): Pick<SenderOption, "enabled" | "note"> => {
	const canLinkedin = hasLinkedin(user) && !!lead.prospectHasLinkedinUrl;
	const canEmail = hasMailbox(user) && !!lead.prospectHasEmail;

	if (canLinkedin || canEmail) {
		return { enabled: true, note: linkedinDisconnected ? "linkedinDisconnected" : null };
	}

	// Reconnecting LinkedIn is what would make this sender usable: the lead has a profile, or
	// the sender has no mailbox to fall back on.
	if (linkedinDisconnected && (lead.prospectHasLinkedinUrl || !hasMailbox(user))) {
		return { enabled: false, note: "linkedinDisconnected" };
	}

	if (!hasMailbox(user) && !hasLinkedin(user)) {
		return { enabled: false, note: "nothingConnected" };
	}

	if (hasMailbox(user) && hasLinkedin(user)) {
		return { enabled: false, note: "noChannelForLead" };
	}

	return { enabled: false, note: hasMailbox(user) ? "noEmailForLead" : "noLinkedinForLead" };
};

/**
 * Every colleague the sender picker lists for one lead (PREC-4244). A sender is enabled when
 * they can reach the lead on at least one channel: an email-only sender for a lead with an email
 * address, too. The others stay listed, disabled, with the reason: a logged-out LinkedIn, nothing
 * connected, or a channel the lead lacks. The picker used to keep only senders with a connected
 * LinkedIn and dropped the rest without a word.
 *
 * Order: enabled senders, then the ones this lead or a reconnect would fix, then the users who
 * have nothing connected.
 */
export const buildSenderOptions = (
	sendingUsers: User[],
	candidates: SenderCandidate[] | undefined,
	lead: LeadChannels
): SenderOption[] => {
	const disconnected = new Set(
		(candidates ?? []).filter((c) => c.linkedin === "disconnected").map((c) => c.id)
	);
	const sendingIds = new Set(sendingUsers.map((user) => user.id));

	const fromSendingUsers: SenderOption[] = sendingUsers.map((user) => ({
		id: user.id,
		...namesOf(user),
		user,
		...judgeSendingUser(user, disconnected.has(user.id), lead),
	}));

	// Not a sending user: no mailbox, and a LinkedIn account that is logged out or missing.
	const unableToSend: SenderOption[] = (candidates ?? [])
		.filter((candidate) => !sendingIds.has(candidate.id) && candidate.linkedin !== "connected")
		.map((candidate) => ({
			id: candidate.id,
			...namesOf(candidate),
			user: null,
			enabled: false,
			note: candidate.linkedin === "disconnected" ? "linkedinDisconnected" : "nothingConnected",
		}));

	const rank = (option: SenderOption): number => {
		if (option.enabled) {
			return 0;
		}

		return option.note === "nothingConnected" ? 2 : 1;
	};

	return [...fromSendingUsers, ...unableToSend]
		.map((option, index) => ({ option, index }))
		.sort((a, b) => rank(a.option) - rank(b.option) || a.index - b.index)
		.map(({ option }) => option);
};

/** The senders that can be chosen for this lead, in picker order. */
export const selectableSenders = (options: SenderOption[]): User[] =>
	options.filter((option) => option.enabled && option.user).map((option) => option.user as User);
